import { LOCALES, DEFAULT_LOCALE, type Locale } from '@/strings/types';

/* The public origin. Every absolute URL the site emits about itself (canonical,
 * hreflang alternates, sitemap) is built from this, so the apex-vs-www decision
 * lives in exactly one place. */
export const SITE_ORIGIN = 'https://prinotes.com';

/* Every public route stem, mirrored at /en/<stem> and /ar/<stem> per L11.
 * '' is the locale home.
 *
 * This is the single source of truth for the per-route canonical + hreflang
 * tags. It MUST stay in sync with the child routes in App.tsx — routes.test.ts
 * fails the build if it drifts. A page missing here silently ships with no
 * canonical at all, which is the failure mode this list exists to prevent.
 *
 * public/sitemap.xml is currently hand-written against this same list; keep the
 * two aligned when adding a page (Phase 4 adds pricing, cookies, compliance). */
export const ROUTE_STEMS = [
  '',
  'privacy',
  'terms',
  'subprocessors',
  'about',
  'trust',
] as const;

export type RouteStem = (typeof ROUTE_STEMS)[number];

export function isRouteStem(value: string): value is RouteStem {
  return (ROUTE_STEMS as readonly string[]).includes(value);
}

/* Absolute URL for one page. The home stem keeps its trailing slash (/en/) and
 * the others do not (/en/privacy) — this matches both the router and
 * public/sitemap.xml, and a mismatch would make a page canonicalize to a URL
 * that is not the one in the sitemap. */
export function urlFor(locale: Locale, stem: RouteStem): string {
  return stem === '' ? `${SITE_ORIGIN}/${locale}/` : `${SITE_ORIGIN}/${locale}/${stem}`;
}

/* Splits a router pathname into its locale + stem, or null when the path is not
 * a real page (unknown locale, or an unmatched stem that renders NotFound).
 * Returning null is what keeps 404 URLs from claiming a canonical.
 *
 * Empty segments are rejected rather than collapsed: //en/privacy and
 * /en//privacy render NotFound in the router (an empty segment matches no
 * route), so parsing them as valid here would emit an indexable head under a
 * 404 view — the head/view mismatch caught at Stage 7 of phase
 * 4-seo-quick-wins. Trailing slashes stay tolerated, matching the router
 * (which strips all of them). */
export function parsePath(pathname: string): { locale: Locale; stem: RouteStem } | null {
  const parts = pathname.split('/');
  if (parts[0] !== '') return null; // no leading slash: not a router pathname
  const inner = parts.slice(1);
  while (inner[inner.length - 1] === '') inner.pop(); // trailing slashes ok
  if (inner.some((segment) => segment === '')) return null;

  const [maybeLocale = '', ...rest] = inner;
  const locale = LOCALES.find((l) => l === maybeLocale);
  if (!locale) return null;

  const stem = rest.join('/');
  return isRouteStem(stem) ? { locale, stem } : null;
}

/* canonical + one alternate per locale + x-default.
 *
 * x-default points at the DEFAULT_LOCALE URL rather than a locale-neutral one:
 * bare / 301s straight to /<DEFAULT_LOCALE>/, so there is no neutral URL to
 * name. Default flipped AR -> EN at launch (Phase 1 design, 2026-07-11); it
 * reads from DEFAULT_LOCALE so a future flip back stays a one-line change. */
export function headLinksFor(locale: Locale, stem: RouteStem) {
  return [
    { rel: 'canonical', href: urlFor(locale, stem) },
    ...LOCALES.map((l) => ({ rel: 'alternate', hreflang: l, href: urlFor(l, stem) })),
    { rel: 'alternate', hreflang: 'x-default', href: urlFor(DEFAULT_LOCALE, stem) },
  ];
}
